<script>
	// NPM Modules
	import dayjs from 'dayjs';
	// Stores
	import LinkGroupStore from '@/Stores/LinkGroupStore.js';
	// Constants
	import { SportIcons } from '@/Constants/Icons.js';
	// Components
	import TextInput from '@/Components/TextInput.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	import CheckBox from '@/Components/CheckBox.svelte';
	import Prompt from '@/Components/Prompt.svelte';
	import SelectInput from '@/Components/SelectInput.svelte';
	import FontIcon from '@/Components/FontIcon.svelte';
	import Icon from '@/Components/Icon.svelte';
	import Switch from '@/Components/Switch.svelte';
	import Datepicker from '@/Components/Datepicker.svelte';
	// Services
	import { notify } from '@/Services/Notify.js';

	export let link;
	export let linkGroup;
	export let linkGroupTypeId;
	export let first;
	export let last;

	let sending = false;
	let deleting = false;
	let promptDelete = false;

	let activeDate = dayjs(link.active_from ?? undefined).format('YYYY-MM-DD');
	let activeTime = dayjs(link.active_from ?? undefined).format('HH:mm');
	let expireDate = dayjs(link.active_to ?? undefined).format('YYYY-MM-DD');
	let expireTime = dayjs(link.active_to ?? undefined).format('HH:mm');

	let activeToggle = !!link.active_from;
	let expireToggle = !!link.active_to;

	$: activeStart = dayjs(activeDate + ' ' + activeTime + ':00');
	$: expireStart = dayjs(expireDate + ' ' + expireTime + ':00');
	$: activeStart && expireStart && updateDateTimes();
	$: availableIcons = $LinkGroupStore && doIconMap();
	$: selectedIcon = link && availableIcons && getSelectedIcon();

	function updateDateTimes() {
		if (activeToggle) {
			link.active_from = dayjs(activeStart).format('YYYY-MM-DDTHH:mm:00Z');
		} else {
			link.active_from = null;
		}
		if (expireToggle) {
			link.active_to = dayjs(expireStart).format('YYYY-MM-DDTHH:mm:00Z');
		} else {
			link.active_to = null;
		}
	}

	function doIconMap() {
		let iconArray = [];
		for (const iconName in SportIcons) {
			iconArray.push({ name: iconName, code: SportIcons[iconName] });
		}
		return iconArray;
	}

	function getSelectedIcon() {
		if (link.images.icon) {
			for (const icon of availableIcons) {
				if (link.images.icon === icon.code) {
					return icon;
				} else if (link.images.icon === icon.name) {
					link.images.icon = icon.code;
					return icon;
				}
			}
		}
		return link.images.icon;
	}

	function onSave() {
		sending = true;
		if (!activeToggle) {
			link.active_from = null;
		}
		if (!expireToggle) {
			link.active_to = null;
		}
		LinkGroupStore.updateLink(link, linkGroupTypeId)
			.then(() => {
				notify({
					type: 'success',
					title: 'Link',
					message: 'Link updated',
					timeout: 3
				});
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Failed to update Link',
					timeout: 5
				});
			})
			.finally(() => (sending = false));
	}

	function onDelete() {
		deleting = true;
		LinkGroupStore.deleteLink(link, linkGroup.id)
			.then(() => {
				notify({
					type: 'success',
					title: 'Link',
					message: 'Link Deleted',
					timeout: 3
				});
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Failed to delete the Link',
					timeout: 5
				});
			})
			.finally(() => (deleting = false));
	}

	function shiftOrderUp() {
		LinkGroupStore.changeLinkOrder(linkGroup.id, link.order, link.order - 1);
	}

	function shiftOrderDown() {
		LinkGroupStore.changeLinkOrder(linkGroup.id, link.order, link.order + 1);
	}
</script>

<Prompt
	bind:open={promptDelete}
	title="Confirm delete"
	message="This can't be undone. Are you sure?"
	onConfirm={() => onDelete()}
/>

<div class="pt-2 px-2 bg-white border">
	<div class="flex flex-row">
		<div class="flex-1">
			<div class="flex space-x-2">
				<div>
					<div class="flex flex-col items-center justify-center">
						<button
							on:click={() => shiftOrderUp()}
							disabled={link.order === first}
							class:opacity-0={link.order === first}
						>
							<Icon name="up-arrow" classes="w-7 h-7" />
						</button>
						<span class="my-px">{link.order}</span>
						<button
							on:click={() => shiftOrderDown()}
							disabled={link.order === last}
							class:opacity-0={link.order === last}
						>
							<Icon name="down-arrow" classes="w-7 h-7" />
						</button>
					</div>
				</div>
				<div class="grabber cursor-move">
					<div class="flex flex-col items-center place-content-center justify-center align-middle cursor-move text-3">
						<Icon name="drag-drop" classes="w-4 h-4 mt-6 icon" />
					</div>
				</div>
				<TextInput bind:value={link.name} label="Name" name={`${link.id ?? link.temp_id}-name`} />
				<TextInput bind:value={link.url} label="URL" name={`${link.id ?? link.temp_id}-url`} />
				{#if selectedIcon && selectedIcon.code}
					<div class="w-10 mx-2 mt-6 pt-1 text-center">
						{#key selectedIcon.code}
							<FontIcon icon={selectedIcon.code} className="icon text-3xl" />
						{/key}
					</div>
				{:else}
					<div class="w-10 mx-2" />
				{/if}
				<SelectInput
					label="Icon"
					name={`${link.id ?? link.temp_id}-icon`}
					required={true}
					bind:value={link.images.icon}
				>
					<option value={undefined}>Select Icon...</option>
					<option value={null}>No icon</option>
					{#each availableIcons as icon (icon.name)}
						{#if icon.name === link.images.icon || icon.code === link.images.icon}
							<option value={icon.code} selected>{icon.name}</option>
						{:else}
							<option value={icon.code}>{icon.name}</option>
						{/if}
					{/each}
				</SelectInput>
				<div class="flex flex-grow">
					<div class="w-1/2">
						<div class="flex flex-row">
							<label class="form-label" for={`${link.id}-activeTime`}>Publish at:</label>
							<Switch name={`${link.id}-activeToggle`} bind:checked={activeToggle} small={true} className="ml-1" />
						</div>
						<div class="flex flex-row items-center justify-center min-w-[240px]">
							{#if activeToggle}
								<Datepicker
									selected={activeDate}
									displayFormat="DD-MM-YYYY"
									dateChosen={(date) => {
										activeDate = dayjs(date).format('YYYY-MM-DD');
									}}
								/>
								<TextInput bind:value={activeTime} name={`${link.id ?? link.temp_id}-activeTime`} type="time" />
							{/if}
						</div>
					</div>
					<div class="w-1/2">
						<div class="flex flex-row">
							<label class="form-label" for={`${link.id}-expireTime`}>Expires:</label>
							<Switch name={`${link.id}-expireToggle`} bind:checked={expireToggle} small={true} className="ml-1" />
						</div>
						<div class="flex flex-row items-center justify-center min-w-[240px]">
							{#if expireToggle}
								<Datepicker
									selected={expireDate}
									displayFormat="DD-MM-YYYY"
									dateChosen={(date) => {
										expireDate = dayjs(date).format('YYYY-MM-DD');
									}}
								/>
								<TextInput bind:value={expireTime} name={`${link.id ?? link.temp_id}-expireTime`} type="time" />
							{/if}
						</div>
					</div>
				</div>
				<div class="flex flex-col items-center justify-self-end">
					<div class="form-label">Active</div>
					<div class="flex flex-1">
						<CheckBox bind:checked={link.active} />
					</div>
				</div>
				<div class="flex flex-col items-center pl-2 justify-self-end">
					<div class="form-label">Public</div>
					<div class="flex flex-1">
						<CheckBox bind:checked={link.is_public} />
					</div>
				</div>
			</div>
		</div>
		<div class="ml-5 -mt-1 mb-1">
			<div class="flex flex-col flex-shrink justify-center space-y-2">
				<LoadingButton on:click={onSave} className="btn btn-primary" loading={sending}>
					{link.temp_id ? 'Create' : 'Save'}
				</LoadingButton>
				<LoadingButton
					on:click={() => (link.new ? onDelete() : (promptDelete = true))}
					className="btn btn-primary-action"
					loading={deleting}
					>Delete
				</LoadingButton>
			</div>
		</div>
	</div>
</div>
